// bfsg

import * as React from 'react'
import styled from 'styled-components'
import usePromotions from 'hooks/account/cart/usePromotionCodes'
import ArrowDown from 'assets/arrow-down.svg'
import ArrowUp from 'assets/arrow-up.svg'
import TickIcon from 'assets/tick-icon.svg'
import TailSpin from 'assets/tail-spin.svg'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import Input from 'theme/atoms/Input'

export default function PromotionInput() {
  const t = useTranslations<'molecules-account-CartSummary'>()
  const [expanded, setExpanded] = React.useState(false)
  const promotions = usePromotions()
  const [code, setCode] = React.useState('')
  const [staticCode, setStaticCode] = React.useState('')

  const addCode = () => {
    if (code === '') return
    promotions.addCode(code)
    setStaticCode(code)
  }

  const wasSend = code !== '' && staticCode === code && !promotions.adding

  const msg = React.useMemo(() => {
    const { added, excluded } = promotions.status
    const codeExists = !!promotions.items.find(
      (item) => code !== '' && item.code === code
    )
    if (code === '')
      return {
        snippet: '',
        error: false
      }
    if (promotions.notFoundError)
      return {
        snippet: t('promotioninput_voucher_failure_text', { couponCode: code }),
        error: true
      }
    if (added && excluded && !codeExists)
      return {
        snippet: t('promotioninput_voucher_exluded_text'),
        error: true
      }
    if (!added || !codeExists)
      return {
        snippet: t('promotioninput_voucher_not_eligable'),
        error: true
      }

    return {
      snippet: t('promotioninput_voucher_success_text'),
      error: false
    }
  }, [promotions.notFoundError, promotions.items, promotions.status])

  React.useEffect(() => {
    if (!wasSend || code === '') {
      msg.error = false
    }
  }, [wasSend, code])

  return (
    <Wrapper className="PromotionInput">
      <button
        className="title-wrapper"
        data-cy-handle={'coupon-expand-toggle'}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="title">{t('promotioninput_voucher_input')}</div>
        {!expanded ? <ArrowDown data-cy-state="show-arrow-up" /> : <ArrowUp />}
      </button>
      {expanded && (
        <div className="coupon-wrapper" data-cy-state={'show-coupon-input'}>
          <div className="coupon-input">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                addCode()
              }}
            >
              <Input
                type="text"
                className="coupon-input"
                data-cy-handle="coupon-input"
                data-cy-state="coupon-add-error-message"
                value={code}
                placeholder={t.asText(false, 'promotioninput_coupon_input')}
                error={msg.error ? msg.snippet : ''}
                onChange={(e) => setCode(e)}
              />
            </form>
            <button
              className={
                wasSend && !msg.error
                  ? 'coupon-submit success'
                  : 'coupon-submit'
              }
              data-cy-handle="coupon-action-btn"
              title="submit coupon code"
              onClick={() => addCode()}
            >
              {promotions.adding ? (
                <TailSpin data-cy-state="coupon-adding" />
              ) : wasSend && !msg.error ? (
                <TickIcon
                  className={'success'}
                  data-cy-state="coupon-add-success-icon"
                />
              ) : (
                <ArrowDown />
              )}
            </button>
            {wasSend && (
              <div
                data-cy-state="coupon-add-message"
                className="status-message"
              >
                {!msg.error && (
                  <div
                    className="success"
                    data-cy-state="coupon-add-success-message"
                  >
                    {msg.snippet}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  cursor: pointer;
  border: 1px solid ${theme.colors.shade.b6};
  padding: ${theme.spacing('m')};
  width: 100%;
  background: ${theme.colors.white};
  > .title-wrapper {
    display: flex;
    width: 100%;
    ${theme.ty('r-s', '700', 'r-base')};

    > .title {
      flex: 1;
    }
  }
  > .coupon-wrapper {
    > .coupon-input {
      width: 100%;
      min-height: 3.125rem;
      position: relative;
      margin-top: ${theme.spacing('m')};
      > .status-message {
        margin-top: ${theme.spacing('xxs')};
        > .success {
          ${theme.ty('r-s')};
          color: ${theme.colors.accent.green};
          border-color: ${theme.colors.accent.green};
        }
        > .error {
          color: ${theme.colors.accent.red};
          ${theme.ty('r-xs', null, 'r-s')};
          padding: 0 ${theme.spacing('s')};
        }
      }

      > .coupon-submit {
        position: absolute;
        right: 0px;
        top: 0px;
        background: ${theme.colors.b0};
        width: 3.125rem;
        height: 3.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0 0.3125rem 0.3125rem 0;
        &.success {
          background: ${theme.colors.accent.green};
        }
        > svg {
          transform: rotate(-90deg);
          &.success {
            transform: rotate(0deg);
          }
          > g #arrow-down {
            fill: white;
          }
        }
      }
    }
  }
`
